import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "./motion";
import { useLang } from "../../i18n";

const PORTRAIT = "https://customer-assets-m6fa6gv7.emergentagent.net/job_acct-solutions-3/artifacts/e9c93jyz_Office%20pic.jpeg";

export const About = () => {
  const { c } = useLang();
  const a = c.about;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="relative py-24 md:py-36 bg-[var(--bg-primary)]">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <Overline>{a.overline}</Overline>
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight mt-5 leading-[0.92] max-w-4xl">
            {a.headingA}<span className="italic text-[var(--accent)]">{a.headingAccent}</span>{a.headingB}
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 mt-10 max-w-5xl">
            <p className="font-sans text-base md:text-lg leading-relaxed text-[var(--text-secondary)]">{a.p1}</p>
            <p className="font-sans text-base md:text-lg leading-relaxed text-[var(--text-secondary)]">{a.p2}</p>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mt-14 bg-[var(--border)] border border-[var(--border)]">
            {a.facts.map((f) => (
              <div key={f.label} data-testid={`about-fact-${f.value}`} className="bg-[var(--bg-primary)] p-6 md:p-8">
                <div className="font-serif text-2xl md:text-4xl text-[var(--text-primary)]">{f.value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-secondary)] mt-3 leading-relaxed">{f.label}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <div className="mt-20 md:mt-28 pt-16 border-t border-[var(--border)] grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <Reveal>
                <Overline>{a.founderOverline}</Overline>
                <h2 className="font-serif text-4xl md:text-6xl tracking-tight mt-5 leading-[0.95]">
                  {a.founderName[0]}<br />{a.founderName[1]}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 relative overflow-hidden rounded-sm border border-[var(--border)] aspect-[4/5]">
                  <motion.img style={{ y }} src={PORTRAIT} alt="Sony Ancion" className="absolute -top-[8%] left-0 w-full h-[120%] object-cover object-[center_25%]" />
                  <div className="absolute inset-0 ring-1 ring-inset ring-white/5" />
                </div>
                <p className="font-mono text-xs text-[var(--text-secondary)] mt-4 uppercase tracking-[0.15em]">
                  {a.caption}
                </p>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col justify-center gap-14 md:gap-20 pt-4">
            {a.chapters.map((ch, i) => (
              <Reveal key={ch.n} delay={i * 0.05}>
                <div className="border-t border-[var(--border)] pt-8">
                  <div className="flex items-baseline gap-6">
                    <span className="font-mono text-sm text-[var(--accent)]">{ch.n}</span>
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl text-[var(--text-primary)]">{ch.title}</h3>
                      <p className="font-sans text-base md:text-lg leading-relaxed text-[var(--text-secondary)] mt-4 max-w-xl">
                        {ch.body}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
